import { useState } from "react"
import { Users, Package, FolderTree, ShoppingCart, MessageSquare, Search, Settings, PlusCircle, BarChart3, ExternalLink, LogOut, TrendingUp, Truck, MapPin } from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthContext"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"

interface AdminSidebarProps {
  stats: {
    totalUsers: number;
    totalProducts: number;
    totalCategories: number;
    totalOrders: number;
    pendingQuotes: number;
    pendingSpecialRequests: number;
    pendingContactRequests: number;
    totalRevenue: number;
  };
  loading: boolean;
  profile?: {
    first_name?: string;
    last_name?: string;
  };
}

const adminItems = [
  {
    id: 'overview',
    title: 'סקירה כללית',
    url: '/admin?tab=overview',
    icon: BarChart3,
    statKey: null
  },
  {
    id: 'orders',
    title: 'ניהול הזמנות',
    url: '/admin?tab=orders',
    icon: ShoppingCart,
    statKey: 'totalOrders'
  },
  {
    id: 'users',
    title: 'ניהול משתמשים',
    url: '/admin?tab=users',
    icon: Users,
    statKey: 'totalUsers'
  },
  {
    id: 'products',
    title: 'ניהול מוצרים',
    url: '/admin?tab=products',
    icon: Package,
    statKey: 'totalProducts'
  },
  {
    id: 'categories',
    title: 'ניהול קטגוריות',
    url: '/admin?tab=categories',
    icon: FolderTree,
    statKey: 'totalCategories'
  },
  {
    id: 'suppliers',
    title: 'ניהול ספקים',
    url: '/admin?tab=suppliers',
    icon: Truck,
    statKey: null
  },
  {
    id: 'cities',
    title: 'ניהול אזורי משלוח',
    url: '/admin?tab=cities',
    icon: MapPin,
    statKey: null
  },
  {
    id: 'grades',
    title: 'ניהול דירוגי מוצרים',
    url: '/admin?tab=grades',
    icon: TrendingUp,
    statKey: null
  },
  {
    id: 'contact',
    title: 'יצירת קשר ופניות',
    url: '/admin?tab=contact',
    icon: MessageSquare,
    statKey: null
  },
  {
    id: 'settings',
    title: 'הגדרות אתר',
    url: '/admin?tab=settings',
    icon: Settings,
    statKey: null
  }
]

export function AdminSidebar({ stats, loading, profile }: AdminSidebarProps) {
  const { state, setOpen, isMobile, setOpenMobile } = useSidebar()
  const location = useLocation()
  const { signOut } = useAuth()

  // Close sidebar on mobile when link is clicked
  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const isActive = (url: string) => {
    // Check if we're on a product edit/create route
    if (location.pathname.startsWith('/admin/product/')) {
      const urlParams = new URLSearchParams(url.split('?')[1])
      const urlTab = urlParams.get('tab')
      return urlTab === 'products'
    }

    const urlParams = new URLSearchParams(url.split('?')[1])
    const currentParams = new URLSearchParams(location.search)
    const urlTab = urlParams.get('tab') || 'overview'
    const currentTab = currentParams.get('tab') || 'overview'
    return urlTab === currentTab
  }

  const getNavCls = (url: string) =>
    isActive(url) ? "bg-muted text-primary font-medium" : "hover:bg-muted/50"

  const getStatValue = (statKey: string | null) => {
    if (!statKey || loading) return null
    return stats[statKey as keyof typeof stats]
  }

  return (
    <Sidebar
      className={state === "collapsed" ? "w-14" : "w-72"}
      collapsible="icon"
      side="right"
    >
      {/* Desktop trigger - only visible on larger screens */}
      <SidebarTrigger className="m-2 self-start hidden lg:flex" />

      <SidebarContent>
        {/* Welcome Message */}
        {profile && state !== "collapsed" && (
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm text-muted-foreground text-right">
              ברוך הבא, {profile.first_name} {profile.last_name}
            </p>
          </div>
        )}

        <SidebarGroup>
          <SidebarGroupLabel className="text-right">ניהול מערכת</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {adminItems.map((item) => {
                const statValue = getStatValue(item.statKey)
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={getNavCls(item.url)} onClick={handleLinkClick}>
                        <item.icon className="ml-2 h-4 w-4" />
                        {state !== "collapsed" && (
                          <div className="flex-1 text-right">
                            <span>{item.title}</span>
                            {statValue !== null && (
                              <div className="text-xs text-muted-foreground">
                                {statValue} {item.statKey === 'totalRevenue' ? '₪' : ''}
                              </div>
                            )}
                          </div>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Quick Actions */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-right">פעולות מהירות</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink to="/admin/product/create" className="hover:bg-muted/50" onClick={handleLinkClick}>
                    <PlusCircle className="ml-2 h-4 w-4" />
                    {state !== "collapsed" && <span>הוספת מוצר חדש</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Bottom Actions */}
        <div className="mt-auto">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Button
                      onClick={() => window.open('/', '_blank')}
                      variant="ghost"
                      className="w-full justify-start hover:bg-muted/50"
                    >
                      <ExternalLink className="ml-2 h-4 w-4" />
                      {state !== "collapsed" && <span>בקר באתר</span>}
                    </Button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Button
                      onClick={signOut}
                      variant="ghost"
                      className="w-full justify-start hover:bg-muted/50 text-destructive hover:text-destructive"
                    >
                      <LogOut className="ml-2 h-4 w-4" />
                      {state !== "collapsed" && <span>התנתק</span>}
                    </Button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      </SidebarContent>
    </Sidebar>
  )
}